import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Button,
  Iconography,
  IconographySizes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
} from "@hid-galaxy-ui/galaxy-react";
import "./HealthCheckup.scss";
import axios from "axios";
import { useIsHidStaff } from "../../../../hooks/useIsHidStaff";

interface HealthCheckup {
  id: string;
  apiCategory: string;
  api: string;
  statusCode: number;
  processingTime: number;
  success: boolean;
  errorDetails: string;
  timestamp: string;
}

const HealthCheckUPList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [healthChecks, setHealthChecks] = React.useState<HealthCheckup[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(() => {
    // Initialize from localStorage
    const storedEndTime = localStorage.getItem("health_check_cooldown_end");
    if (storedEndTime) {
      const remaining = Math.ceil(
        (parseInt(storedEndTime) - Date.now()) / 1000
      );
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });

  const isHidStaff = useIsHidStaff();

  // Cooldown timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldownSeconds > 0) {
      timer = setTimeout(() => {
        const newValue = cooldownSeconds - 1;
        setCooldownSeconds(newValue);
        if (newValue <= 0) {
          localStorage.removeItem("health_check_cooldown_end");
        }
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [cooldownSeconds]);

  // Function to start cooldown
  const startCooldown = (seconds: number) => {
    const endTime = Date.now() + seconds * 1000;
    localStorage.setItem("health_check_cooldown_end", endTime.toString());
    setCooldownSeconds(seconds);
  };

  const saveToHistory = (newResults: HealthCheckup[]) => {
    try {
      // Get existing history
      const existingHistory = localStorage.getItem("health_check_history");
      let history: HealthCheckup[] = [];

      if (existingHistory) {
        history = JSON.parse(existingHistory);
      }

      // Append new results to history
      history.push(...newResults);

      // Keep only last 100 records to prevent localStorage from growing too large
      if (history.length > 100) {
        history = history.slice(-100);
      }

      // Save updated history
      localStorage.setItem("health_check_history", JSON.stringify(history));
    } catch (error) {
      console.error("Error saving to history:", error);
    }
  };

  const performHealthChecks = async () => {
    // Check if already in cooldown
    if (cooldownSeconds > 0) {
      console.log("⏳ Cooldown active, skipping refresh");
      return;
    }

    // Start cooldown immediately when clicking refresh
    startCooldown(60);
    setIsLoading(true);

    try {
      // Call the /all-health-checks API
      const response = await axios.post(
        `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/all-health-checks`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("📥 Raw API Response:", response.data);

      if (response.data && response.data.results) {
        const results: HealthCheckup[] = [];
        const apiResults = response.data.results; // Map each API result to HealthCheckup format
        Object.keys(apiResults).forEach((apiKey, index) => {
          const apiData = apiResults[apiKey];

          // Available Categories:
          // - Authentication: auth, login, token related APIs
          // - User Management: user, profile related APIs
          // - Partner: partner, organization related APIs
          // - Invitation: invitation, invite related APIs
          // - Credentials: credential, password, security related APIs
          // - Part Number: part, product, item related APIs
          // - Incident: incident, issue, ticket related APIs
          // - Feature: default category for all other APIs

          // Determine API category based on multiple keywords (priority-based)
          const lowerKey = apiKey.toLowerCase();
          let category = "Feature"; // Default category

          // Check for specific categories (first match wins)
          if (
            lowerKey.includes("auth") ||
            lowerKey.includes("login") ||
            lowerKey.includes("token")
          ) {
            category = "Authentication";
          } else if (
            lowerKey.includes("user") ||
            lowerKey.includes("profile")
          ) {
            category = "User Management";
          } else if (
            lowerKey.includes("partner") ||
            lowerKey.includes("organization")
          ) {
            category = "Partner";
          } else if (
            lowerKey.includes("invitation") ||
            lowerKey.includes("invite") ||
            lowerKey.includes("inv")
          ) {
            category = "Invitation";
          } else if (
            lowerKey.includes("credential") ||
            lowerKey.includes("password") ||
            lowerKey.includes("security")
          ) {
            category = "Credentials";
          } else if (
            lowerKey.includes("part") ||
            lowerKey.includes("product") ||
            lowerKey.includes("item")
          ) {
            category = "Part Number";
          } else if (
            lowerKey.includes("incident") ||
            lowerKey.includes("issue") ||
            lowerKey.includes("ticket")
          ) {
            category = "Incident";
          }

          // Format API name from camelCase to Title Case
          const apiName = apiKey
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();

          // Get error details if any
          let errorDetails = "";
          if (!apiData.success && apiData.data) {
            if (apiData.data.message) {
              errorDetails = apiData.data.message;
            } else if (apiData.data.details) {
              errorDetails =
                apiData.data.details.detail ||
                JSON.stringify(apiData.data.details);
            } else if (apiData.data.error) {
              errorDetails =
                typeof apiData.data.error === "string"
                  ? apiData.data.error
                  : "API Error";
            }
          }

          results.push({
            id: `${Date.now()}-${index}`,
            apiCategory: category,
            api: apiName,
            statusCode: apiData.status || 0,
            processingTime: apiData.responseTime
              ? apiData.responseTime / 1000
              : 0,
            success:
              apiData.success === true &&
              apiData.status >= 200 &&
              apiData.status < 300,
            errorDetails: errorDetails,
            timestamp: new Date().toISOString(),
          });
        });

        // Update state with the results
        setHealthChecks(results);
        localStorage.setItem("health_checkups", JSON.stringify(results));
        saveToHistory(results);
        window.dispatchEvent(new Event("healthChecksUpdated"));

        console.log("✅ Health checks loaded successfully:", results);
      }
    } catch (error: any) {
      console.error("❌ Error fetching health checks:", error);
    } finally {
      setIsLoading(false);
    }
  }; // Load health checkups from api-status on page load
  useEffect(() => {
    const loadHealthChecks = async () => {
      // Fetch the latest data from api-status
      try {
        const statusResponse = await axios.get(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/api-status`
        );
        console.log("📥 API Status Response:", statusResponse.data);

        if (
          statusResponse.data &&
          Array.isArray(statusResponse.data) &&
          statusResponse.data.length > 0
        ) {
          // Sort by updatedAt to get the latest timestamp group
          const sortedData = [...statusResponse.data].sort((a, b) => {
            const dateA = new Date(a.updatedAt.replace(" ", "T"));
            const dateB = new Date(b.updatedAt.replace(" ", "T"));
            return dateB.getTime() - dateA.getTime();
          });

          // Get the latest timestamp
          const latestTimestamp = sortedData[0].updatedAt;
          setLastUpdated(latestTimestamp);
          console.log("✅ Latest updated timestamp:", latestTimestamp);

          // Group by timestamp to find all records from the latest iteration
          const groupedByTimestamp = sortedData.reduce((acc, item) => {
            const timestamp = new Date(item.updatedAt.replace(" ", "T"));
            // Round to the exact minute (remove seconds and milliseconds)
            timestamp.setSeconds(0, 0);
            const timeKey = timestamp.toISOString();

            if (!acc[timeKey]) {
              acc[timeKey] = [];
            }
            acc[timeKey].push(item);
            return acc;
          }, {} as Record<string, any[]>);

          // Get the first (latest) group of records
          const timestampKeys = Object.keys(groupedByTimestamp);
          const latestRecords =
            timestampKeys.length > 0
              ? groupedByTimestamp[timestampKeys[0]]
              : [];

          // Map api-status data to HealthCheckup format
          const results: HealthCheckup[] = latestRecords.map(
            (record: any, index: number) => {
              // Determine API category based on feature field
              const lowerFeature = record.feature.toLowerCase();
              let category = "Feature"; // Default category

              if (
                lowerFeature.includes("auth") ||
                lowerFeature.includes("login") ||
                lowerFeature.includes("token")
              ) {
                category = "Authentication";
              } else if (
                lowerFeature.includes("user") ||
                lowerFeature.includes("profile")
              ) {
                category = "User Management";
              } else if (
                lowerFeature.includes("partner") ||
                lowerFeature.includes("organization")
              ) {
                category = "Partner";
              } else if (
                lowerFeature.includes("invitation") ||
                lowerFeature.includes("invite") ||
                lowerFeature.includes("inv")
              ) {
                category = "Invitation";
              } else if (
                lowerFeature.includes("credential") ||
                lowerFeature.includes("password") ||
                lowerFeature.includes("security")
              ) {
                category = "Credentials";
              } else if (
                lowerFeature.includes("part") ||
                lowerFeature.includes("product") ||
                lowerFeature.includes("item")
              ) {
                category = "Part Number";
              } else if (
                lowerFeature.includes("incident") ||
                lowerFeature.includes("issue") ||
                lowerFeature.includes("ticket")
              ) {
                category = "Incident";
              }

              // Get error details from statusMessage
              const errorDetails =
                record.statusCode >= 200 && record.statusCode < 300
                  ? ""
                  : record.statusMessage || "";

              // Convert response time from milliseconds to seconds
              let processingTime = 0;
              if (record.responseTime) {
                const timeInMs = parseFloat(
                  record.responseTime.replace("ms", "")
                );
                processingTime = isNaN(timeInMs) ? 0 : timeInMs / 1000;
              }

              return {
                id: record.apiStatusID || `${Date.now()}-${index}`,
                apiCategory: category,
                api: record.feature,
                statusCode: record.statusCode,
                processingTime: processingTime,
                success: record.statusCode >= 200 && record.statusCode < 300,
                errorDetails: errorDetails,
                timestamp: record.updatedAt,
              };
            }
          );

          // Update state with the results
          setHealthChecks(results);
          console.log("✅ Health checks loaded from api-status:", results);
        } else {
          // No data available, check localStorage as fallback
          const storedChecks = localStorage.getItem("health_checkups");
          if (storedChecks) {
            try {
              const parsedChecks = JSON.parse(storedChecks);
              setHealthChecks(Array.isArray(parsedChecks) ? parsedChecks : []);
              console.log(
                "Loaded health checkups from localStorage:",
                parsedChecks
              );
            } catch (error) {
              console.error(
                "Error parsing health checkups from localStorage:",
                error
              );
              setHealthChecks([]);
            }
          } else {
            setHealthChecks([]);
          }
        }
      } catch (error) {
        console.error("❌ Error fetching api-status:", error);

        // Fallback to localStorage if API fails
        const storedChecks = localStorage.getItem("health_checkups");
        if (storedChecks) {
          try {
            const parsedChecks = JSON.parse(storedChecks);
            setHealthChecks(Array.isArray(parsedChecks) ? parsedChecks : []);
            console.log(
              "Loaded health checkups from localStorage (fallback):",
              parsedChecks
            );
          } catch (error) {
            console.error(
              "Error parsing health checkups from localStorage:",
              error
            );
            setHealthChecks([]);
          }
        } else {
          setHealthChecks([]);
        }
      }
    };

    loadHealthChecks();

    // Listen for storage changes
    window.addEventListener("storage", loadHealthChecks);

    // Custom event for same-window updates
    const handleHealthChecksUpdate = () => loadHealthChecks();
    window.addEventListener("healthChecksUpdated", handleHealthChecksUpdate);

    return () => {
      window.removeEventListener("storage", loadHealthChecks);
      window.removeEventListener(
        "healthChecksUpdated",
        handleHealthChecksUpdate
      );
    };
  }, []);

  const filteredHealthChecks = healthChecks.filter((check) => {
    const matchesSearch =
      searchTerm.length === 0 ||
      check.apiCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.api.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.statusCode.toString().includes(searchTerm) ||
      check.errorDetails.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const paginatedData = filteredHealthChecks.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const getStatusBadge = (statusCode: number, success: boolean) => {
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Positive}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (statusCode >= 400 && statusCode < 500) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Caution}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (statusCode >= 500) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Danger}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Info}
          iconType={InlineStatusIconType.Dot}
        />
      );
    }
  };

  const getSuccessIndicator = (success: boolean) => {
    if (success) {
      return (
        <span className="success-indicator success">
          <Iconography icon="circleCheck" size={IconographySizes.Small} />
          Success
        </span>
      );
    } else {
      return (
        <span className="success-indicator failure">
          <Iconography icon="circleXmark" size={IconographySizes.Small} />
          Failed
        </span>
      );
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      Authentication: "#00aeef",
      "User Management": "#94bb20",
      Partner: "#f8971d",
      Incident: "#d31245",
      Feature: "#8b4789",
    };
    const color = colors[category] || "#666666";
    return (
      <span
        className="category-badge"
        style={{
          backgroundColor: `${color}20`,
          color: color,
          borderColor: color,
        }}
      >
        {category}
      </span>
    );
  };

  return (
    <div className="health-checkup-list-container">
      <div className="health-checkup-list-header">
        <div className="left">
          <h2>Health Checkup</h2>
          <p className="subtitle">Monitor API health and performance metrics</p>
        </div>
        <div className="right">
          {lastUpdated && (
            <span className="last-updated-badge">
              Last updated:{" "}
              {new Date(lastUpdated.replace(" ", "T")).toLocaleString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })}
            </span>
          )}
          <Button
            variant="tertiary"
            label="History"
            icon={
              <Iconography
                icon="clockRotateLeft"
                size={IconographySizes.Medium}
              />
            }
            onClick={() => {
              const basePath = location.pathname.replace(
                /\/health-checkup$/,
                ""
              );
              navigate(`${basePath}/health-check-history`);
            }}
          />
          {isHidStaff && (
            <Button
              variant="secondary"
              label={
                isLoading
                  ? "Checking..."
                  : cooldownSeconds > 0
                  ? `Wait ${cooldownSeconds}s`
                  : "Refresh"
              }
              disabled={isLoading || cooldownSeconds > 0}
              icon={
                <Iconography
                  icon="arrowsRotate"
                  size={IconographySizes.Medium}
                />
              }
              onClick={performHealthChecks}
            />
          )}
        </div>
      </div>

      <div className="health-checkup-stats">
        <div className="stat-card success">
          <Iconography icon="circleCheck" size={IconographySizes.Large} />
          <div className="stat-content">
            <div className="stat-value">
              {healthChecks.filter((h) => h.success).length}
            </div>
            <div className="stat-label">Successful</div>
          </div>
        </div>
        <div className="stat-card failed">
          <Iconography icon="circleXmark" size={IconographySizes.Large} />
          <div className="stat-content">
            <div className="stat-value">
              {healthChecks.filter((h) => !h.success).length}
            </div>
            <div className="stat-label">Failed</div>
          </div>
        </div>
        <div className="stat-card total">
          <Iconography icon="heartPulse" size={IconographySizes.Large} />
          <div className="stat-content">
            <div className="stat-value">{healthChecks.length}</div>
            <div className="stat-label">Total APIs</div>
          </div>
        </div>
        <div className="stat-card avg-time">
          <Iconography icon="clock" size={IconographySizes.Large} />
          <div className="stat-content">
            <div className="stat-value">
              {healthChecks.length > 0
                ? Math.round(
                    healthChecks.reduce((sum, h) => sum + h.processingTime, 0) /
                      healthChecks.length
                  )
                : 0}
              s
            </div>
            <div className="stat-label">Avg Response Time</div>
          </div>
        </div>
      </div>

      <div className="health-checkup-table-container">
        <div className="table-actions">
          <h3>Health Check Monitor</h3>
          {/* <Button
            variant="secondary"
            label="History"
            icon={
              <Iconography
                icon="clockRotateLeft"
                size={IconographySizes.Small}
              />
            }
            onClick={() => navigate("health-check-history")}
            className="history-button"
          /> */}
        </div>
        <div className="health-checkup-table">
          <div className="table-header">
            <div className="col col-category">API Category</div>
            <div className="col col-api">API</div>
            <div className="col col-status">Status Code</div>
            <div className="col col-time">Processing Time</div>
            <div className="col col-success">Success</div>
            <div className="col col-error">Error Details</div>
          </div>

          {paginatedData.length > 0 ? (
            paginatedData.map((check, index) => (
              <div className="table-row" key={check.id}>
                <div className="col col-category">
                  {getCategoryBadge(check.apiCategory)}
                </div>
                <div className="col col-api" title={check.api}>
                  <code>{check.api}</code>
                </div>
                <div className="col col-status">
                  {getStatusBadge(check.statusCode, check.success)}
                </div>
                <div className="col col-time">
                  <span
                    className={`time-badge ${
                      check.processingTime > 2 ? "slow" : ""
                    }`}
                  >
                    {check.processingTime.toFixed(2)}s
                  </span>
                </div>
                <div className="col col-success">
                  {getSuccessIndicator(check.success)}
                </div>
                <div className="col col-error" title={check.errorDetails}>
                  {check.errorDetails || "-"}
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">
              <div className="no-data-icon">
                <Iconography
                  icon="heartPulse"
                  size={IconographySizes.XXLarge}
                />
              </div>
              <h3>No Health Check Data</h3>
              <p>
                {searchTerm
                  ? "No health checks match your search criteria. Try adjusting your search."
                  : "No health check data available. System monitoring will begin shortly."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthCheckUPList;
